// Used in composite-assignment-submit.component.html, singular-assignment.component.html, composite-tasks.component.html, and activity-preview-dialog.component.html

// Angular
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { StepperSelectionEvent } from "@angular/cdk/stepper";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { FileUploader } from "ng2-file-upload";

// Services/Types/Helpers
import { TimerService } from "#core/services/timer.service";
import { CookieService } from "#services/cookie.service";
import { LocalStorage } from "#services/local-storage.service";
import { TechCheckService } from "#services/tech-check.service";
import {
    PPAssignment as Assignment,
    CompositeTaskAssignment,
    Course,
} from "cli-types";

// RXJS/Store
import { Perform, PerformAssignmentPayload } from "@carnegie-learning/cure-types";
import { AvenueService } from "@carnegie-learning/data-access-angular-passport";
import { Observable, Subject, Subscription, timer } from "rxjs";
import { map, takeUntil, takeWhile } from "rxjs/operators";

@Component({
    selector: "avenue-viewer",
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: "./avenue-viewer.component.html",
    styleUrls: ["./avenue-viewer.component.scss"],
    standalone: false
})
export class AvenueViewerComponent implements OnInit, AfterViewInit, OnDestroy {
    private assetVideoElementRef: ElementRef;
    private countdown = new Subject<number>();
    private ngUnsubscribe = new Subject();
    private timerSubscription: Subscription | null = null;

    // Used by the preview function to set the task without needing an assignment.
    @Input() performTask: Perform;
    @Input() currentCourse?: Course;
    @Input() assignment: Assignment | CompositeTaskAssignment;

    @Output() submitAssignment = new EventEmitter<PerformAssignmentPayload>();
    @Output() setNoPendingChanges = new EventEmitter<boolean>();

    @ViewChild("video") videoElementRef: ElementRef;
    @ViewChild("uploadedVideo")
    uploadedVideoElementRef: ElementRef;
    // Use view children and subscribe because we turn the video on/off via ngIf
    @ViewChildren("assetVideo") assetVideoChildren: QueryList<ElementRef>;

    mediaUrl$: Observable<string>;
    isSecureOrigin: boolean;
    errorMsg: string;
    viewsRemaining: number;
    recordingTries: number;
    stepIndex: number; // = 0?
    // NOTE: The next line was an implicit any, which I converted to File, as that's how it appears to get populated later.
    uploadedFile: File; // for ios and safari
    assignmentPayload: PerformAssignmentPayload;
    studentVideoName: string;
    videoElement: HTMLVideoElement;
    uploadedVideo: HTMLVideoElement;
    assetVideoElement: HTMLVideoElement;
    mediaRecorder: MediaRecorder;
    recordedBlobs: Blob[];
    isRecording = false;
    downloadUrl: string;
    stream: MediaStream | null;
    isChromeIpadUser: boolean; // jfs: testing video error
    isIosMobile: boolean;
    showAsset = true;
    percentTime = 0;
    feedbackForm: UntypedFormGroup;
    videoBuffer: Blob;
    submitClicked = false;
    countdownTimer: number;
    showOverlay = false;
    disablePause = false;
    isComposite = false;
    maxAttempts = 1;
    mimeTypes = ["video/quicktime", "video/mp4", "video/webm"];
    uploader: FileUploader;
    recordingOpts = {
        audio: {
            echoCancellation: { exact: true },
        },
        video: {
            width: 360,
        },
    };
    smallScreen: boolean;
    isPaused: boolean = false;
    recordingTimeIndicator: number;
    totalRecordingTime: number;

    constructor(
        private fb: UntypedFormBuilder,
        private cdr: ChangeDetectorRef,
        public localStorage: LocalStorage,
        private breakpointObserver: BreakpointObserver,
        private techChecker: TechCheckService,
        private avenueTasksService: AvenueService,
        private cookieService: CookieService,
        private timerService: TimerService
    ) {
        // window.isSecureContext could be used for Chrome
        this.isSecureOrigin =
            location.protocol === "https:" ||
            location.host.includes("localhost");

        breakpointObserver
            .observe([
                Breakpoints.XSmall,
                Breakpoints.Small,
                Breakpoints.Medium,
            ])
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((result) => {
                //  this.smallScreen = result.matches;
            });
    }

    ngOnInit() {
        // jfs: checks if user is viewing video on ipad chrome and gives error message.
        this.isIosMobile = /ipad|iPhone/i.test(
            navigator.userAgent.toLowerCase()
        );

        const techInfo = this.techChecker.getDeviceInfo();

        if (this.isIosMobile && navigator.userAgent.match("CriOS")) {
            this.isChromeIpadUser = true;
        } else if (
            typeof MediaRecorder === "undefined" ||
            techInfo.browser === "Safari"
        ) {
            // MediaRecorder is not supported in Safari and iOS devices :(
            this.isChromeIpadUser = true;
            this.isIosMobile = true;
        }

        if (!this.performTask) {
            if (
                this.assignment &&
                (<CompositeTaskAssignment>this.assignment)
                    .compositeTaskAssignmentID
            ) {
                const assnmnt = <CompositeTaskAssignment>this.assignment;
                this.performTask = <Perform>assnmnt.asset;
                this.isComposite = true;
            } else {
                const assnmnt = <Assignment>this.assignment;
                this.performTask = <Perform>assnmnt.asset;
            }
        }
        if (this.performTask) {
            this.mediaUrl$ = this.avenueTasksService.getMediaUrl(
                this.performTask.parentMediaAsset
            );
        }

        this.feedbackForm = this.fb.group({
            studentComments: ["", []],
            studentScore: ["", [Validators.required]],
        });

        this.stepIndex = 0;

        if (this.assignment) {
            if (
                this.performTask.views === -1 ||
                this.assignment.maxAttempts === -1
            ) {
                this.viewsRemaining = -1;
            } else {
                this.viewsRemaining = this.assignment.gradeAttempts
                    ? (this.performTask.views || this.assignment.maxAttempts) -
                      this.assignment.gradeAttempts.length
                    : this.performTask.views || this.assignment.maxAttempts;
            }
        }

        // Preview mode, no assignment data found
        if (
            this.assignment !== undefined &&
            this.assignment !== null &&
            this.assignment.maxAttempts
        ) {
            // this just got real, this is not a test or preview but the real deal
            // live assignment mode
            this.maxAttempts = this.assignment.maxAttempts;

            // Edge Case: some AvenueTasks don't have retries associated with DB record, default to 3
            if (!Number.isInteger(this.maxAttempts)) {
                this.maxAttempts = 3;
            }
        } else {
            // Use the default value from the task in practice mode
            if (this.currentCourse && this.currentCourse.maxAttempts) {
                // teacher-class data is found, use teacher-class max attempts
                this.maxAttempts = this.currentCourse.maxAttempts;
            } else {
                // for some reason teacher-class data isn't available so we default to 3 max attempts
                this.maxAttempts = 3;
            }
            this.viewsRemaining = this.performTask.views || this.maxAttempts;
        }

        // Set Recording Tries
        if (
            this.performTask?.recordingTries !== -1 ||
            (!this.performTask?.recordingTries && this.maxAttempts !== -1)
        ) {
            this.recordingTries =
                this.performTask?.recordingTries || this.maxAttempts;
        } else if (
            this.performTask?.recordingTries === -1 ||
            this.maxAttempts === -1
        ) {
            this.recordingTries = -1;
        }

        // don't worry about this in teacher preview mode
        if (this.assignment) {
            const apiURL =
                (<any>window).apiHost +
                "/api/avenue/assignments/" +
                this.assignment.assignmentID +
                "/upload-file";
            const authToken = this.cookieService.get("CL_TOKEN2");

            this.uploader = new FileUploader({
                url: apiURL,
                authToken: authToken,
                autoUpload: false,
                method: "POST",
            });

            this.uploader.onAfterAddingFile = (item) => {
                item.withCredentials = false;
            };
        } else {
            this.uploader = new FileUploader({
                url: (<any>window).apiHost + "/api/avenue/assignments/",
            });
        }
    }

    ngAfterViewInit() {
        if (!this.isSecureOrigin && !this.isIosMobile) {
            this.errorMsg = "Video Recording is only supported over HTTPS.";
            return;
        }

        if (this.isIosMobile) {
            this.uploadedVideo = this.uploadedVideoElementRef.nativeElement;
        } else {
            this.videoElement = this.videoElementRef.nativeElement;
            this.videoElement.muted = true;

            navigator.mediaDevices
                .getUserMedia(this.recordingOpts)
                .then((stream) => {
                    this.stream = stream;
                    this.videoElement.srcObject = this.stream;
                })
                .catch((error) => {
                    if (!this.isIosMobile) {
                        this.errorMsg = "Webcam is unavailable.";
                    }
                    this.cdr.detectChanges();
                });
        }

        this.assetVideoChildren?.changes
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data: QueryList<ElementRef>) => {
                this.assetVideoElementRef = data.first;

                if (this.assetVideoElementRef && this.showAsset) {
                    this.viewAsset();
                }
            });
    }

    ngOnDestroy() {
        if (this.stream) {
            // Close the media stream when component is destroyed
            this.stream.getTracks().forEach((track) => {
                if (track) {
                    track.stop();
                }
            });
            this.stream = null;
        }

        this.videoPause();
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    fileSelected() {
        if (this.uploader.queue.length > 0) {
            // If the Queue is greater than 1, remove the first one.
            if (this.uploader.queue.length > 1) {
                this.uploader.queue[0].remove();
            }
        }
    }

    getMimeTypes() {
        return this.mimeTypes.join(",");
    }

    public onUploaderChange(event: any): void {
        const f = event.srcElement.files[0];

        this.uploadedFile = new File([f], f.name, {
            type: f.type,
        });
        this.uploadedVideo.src = window.URL.createObjectURL(this.uploadedFile);
        this.nextStep();
    }

    videoUploaded() {
        return false;
    }
    stepChange(event: StepperSelectionEvent) {
        this.stepIndex = event.selectedIndex;
        if (this.stepIndex === 1) {
            this.showAsset = true;
            // if (this.performTask.parentMediaAsset.performType === 1) {
            this.viewAsset();
            // }
            if (this.viewsRemaining !== -1) {
                this.viewsRemaining--;
            }
        }

        if (this.stepIndex === 2) {
            this.showAsset = false;
            this.videoPause();
        }

        if (this.stepIndex === 3) {
            this.showAsset = (<Perform>this.performTask).mediaWhileRecording;
            // Every time a student lands on this step, pause button will be 
            // false in order to be able to record
            this.isPaused = false;

            if (this.showAsset) {
                this.viewAsset();
            } else {
                this.videoPause();
            }
            this.viewsRemaining = 0;
            if (this.recordingTries > 0) {
                this.recordingTries--;
            }
            if (!this.isIosMobile) {
                this.recordingCountdownDelay();
            }
            this.setNoPendingChanges.emit(false);
        }

        if (this.stepIndex === 4) {
            // if recording via webcam, stop
            if (!this.isIosMobile) {
                this.stopRecording();
            }
            this.showAsset = false;
            this.videoPause();
        }

        if (this.stepIndex === 5) {
            // this.recordingTries = 0;
            if (this.isRecording) {
                this.stopRecording();
            }
        }
    }

    nextStep() {
        this.stepIndex++;
    }

    viewAsset() {
        this.countdown = new Subject<number>();
        this.percentTime = 0;
        const count = (<Perform>this.performTask).mediaTime * 100;
        timer(0, 10)
            .pipe(
                takeWhile((t) => t < count && this.stepIndex === 1),
                map((t) => {
                    this.percentTime = (t / (1 + count)) * 100;
                    // Update the progress bar.
                    this.cdr.detectChanges();
                    return count - t;
                })
            )
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(
                (t) => this.countdown.next(t),
                null,
                () => {
                    this.countdown.complete();

                    this.percentTime = 0;

                    if (this.stepIndex === 1) {
                        this.nextStep();
                    }
                    this.cdr.detectChanges();
                }
            );

        if (this.performTask.parentMediaAsset.performType === 0) {
            this.assetVideoElement = this.assetVideoElementRef
                ? this.assetVideoElementRef.nativeElement
                : this.assetVideoChildren.first.nativeElement;
            this.assetVideoElement.load();
            this.assetVideoElement.play();
        }
    }

    videoPause() {
        if (this.assetVideoElement) {
            this.assetVideoElement.pause();
        }
    }

    recordingCountdownDelay() {
        if (!this.stream) {
            navigator.mediaDevices
                .getUserMedia(this.recordingOpts)
                .then((stream) => {
                    this.stream = stream;
                    this.startCountdown();
                })
                .catch((error) => {
                    if (!this.isIosMobile) {
                        this.errorMsg = "Webcam is unavailable.";
                    }
                    this.cdr.detectChanges();
                });
        } else {
            this.startCountdown();
        }
    }

    startCountdown() {
        const startTime = 5;
        // Resetting showOverlay so Pause/Record button and Review Recording button are 
        //disabled when student lands on recording step
        this.showOverlay = true;
        this.disablePause = true;

        // Resetting percentTime so that the progress bar is empty when countdown starts
        this.percentTime = 0;

        // Resetting recordingTimeIndicator so that it shows 0:00 when countdown starts
        this.recordingTimeIndicator = 0;

        // Setting totalRecordingTime as soon as countdown starts for the user to see 
        this.totalRecordingTime = (<Perform>this.performTask).recordTime;

        timer(0, 1000)
            .pipe(
                takeWhile((t) => t < startTime),
                map((t) => {
                    this.countdownTimer = startTime - t;
                    // Update the page
                    this.cdr.detectChanges();
                    return this.countdownTimer;
                })
            )
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(
                (t) => this.countdown.next(t),
                null,
                () => {
                    this.countdown.complete();

                    this.showOverlay = false;
                    this.disablePause = false;
                    // Countdown complete. Start recording.
                    if (this.stream) {
                        this.startRecording();
                        this.cdr.detectChanges();
                    }
                }
            );
    }

    togglePause() {
        this.isPaused = !this.isPaused;
        
        if (this.mediaRecorder.state === "recording") {
                this.mediaRecorder.pause();
                this.timerService.pausedOrStoppedTimer();
                this.isRecording = false;
        } else if (this.mediaRecorder.state === "paused") {
                this.mediaRecorder.resume();
                this.timerService.startTimer();
                this.isRecording = true;
        }
    };

    startRecording() {
        // Disabling Pause/Record button and Review Recording button when countdown is complete
        // and recording starts
        this.showOverlay = false;
        this.disablePause = false;

        // Setting percentTime to 0 so that the progress bar is empty when recording starts
        this.percentTime = 0;

        // Setting seconds to milliseconds for the progress bar
        const count = (<Perform>this.performTask).recordTime * 100;

        this.timerService.setTimer(count);
        this.timerSubscription = this.timerService.getCountdownObservable()
            .subscribe(secondsLeft => {

                // Update the progress bar and recording time indicator
                this.percentTime = (count - secondsLeft) / count * 100;
                this.recordingTimeIndicator = (Math.trunc(this.totalRecordingTime - secondsLeft/100));
                this.cdr.detectChanges();

                // If the timer is done, stop recording
                if(secondsLeft === 0) {
                    this.percentTime = 0;
                    this.recordingTimeIndicator = 0;
                    this.timerService.pausedOrStoppedTimer();
                    this.mediaRecorder.stop();
                    this.disablePause = true;
                }
            })
        this.timerService.startTimer();
     
        this.videoElement.srcObject = this.stream;
        this.videoElement.controls = false;
        this.videoElement.muted = true;
        this.recordedBlobs = [];

        const techInfo = this.techChecker.getDeviceInfo();

        let options;
        const browserVersion = Number.parseInt(techInfo.browserVersion, 10);
        if (techInfo.browser === "Firefox" && browserVersion >= 71) {
            // Firefox v71+ introduces new mime requirements (PV2-2286)
            // https://github.com/mozilla/hubs/issues/2024
            options = { mimeType: "video/webm;codecs=vp8,opus" };
        } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp9")) {
            options = { mimeType: "video/webm; codecs=vp9" };
        } else if (MediaRecorder.isTypeSupported("video/webm;codecs=vp8")) {
            options = { mimeType: "video/webm; codecs=vp8" };
        } else if (MediaRecorder.isTypeSupported("video/webm")) {
            options = { mimeType: "video/webm" };
        } else {
            if (!this.isIosMobile) {
                this.errorMsg =
                    "Video Recording is not supported on this browser.";
            }
        }

        try {
            if (this.stream) {
                this.mediaRecorder = new MediaRecorder(this.stream, options);
            }
        } catch (err) {
            // console.log(err);
        }

        this.mediaRecorder.start(); // collect 100ms of data
        this.isRecording = true;
        this.onDataAvailableEvent();
        this.onStopRecordingEvent();
    }

    stopRecording() {
        this.mediaRecorder.stop();
        this.isRecording = false;
        this.timerService.pausedOrStoppedTimer();
    }

    stopRecordingFromViewer() {
        this.mediaRecorder.stop();
        this.isRecording = false;
        try {
            this.mediaRecorder.onstop = (event: Event) => {
                if (this.stream) {
                    this.stream.getTracks().forEach((track) => track.stop());
                }
                this.stream = null;
            };
        } catch (error) {
            // console.log(error);
        }
    }

    playRecording() {
        if (!this.recordedBlobs || !this.recordedBlobs.length) {
            return;
        }

        this.videoElement.src = this.downloadUrl;
        this.videoElement.srcObject = null;

        this.videoElement.controls = true;
        this.videoElement.muted = false;
        this.videoElement.play();
    }

    onDataAvailableEvent() {
        try {
            this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
                if (event.data && event.data.size > 0) {
                    this.recordedBlobs.push(event.data);
                }
            };
        } catch (error) {
            // console.log(error);
        }
    }

    onStopRecordingEvent() {
        try {
            this.mediaRecorder.onstop = (event: Event) => {
                this.videoBuffer = new Blob(this.recordedBlobs, {
                    type: "video/webm",
                });

                this.downloadUrl = window.URL.createObjectURL(this.videoBuffer);
                if (this.stream) {
                    this.stream.getVideoTracks().forEach((track) => {
                        if (track) {
                            track.stop();
                        }
                    });
                }
                this.stream = null;

                // After we stop recording, kick over into play mode.
                this.playRecording();
            };
        } catch (error) {
            // console.log(error);
        }
    }

    taskComplete() {
        if (!this.assignment) {
            // We are in preview mode, do nothing.
            return;
        }

        this.uploader.onSuccessItem = (
            item: any,
            response: any,
            status: any,
            headers: any
        ) => {
            const responseObj = JSON.parse(response);
            const studentVideo =
                responseObj.fileName + "." + responseObj.extension;

            this.studentVideoName = studentVideo;
            this.submitAssignmentData();
        };

        this.uploader.onErrorItem = (
            item: any,
            response: any,
            status: any,
            headers: any
        ) => {};

        if (this.isIosMobile) {
            this.uploader.addToQueue([this.uploadedFile]);
        } else {
            const file = new File([this.videoBuffer], "recording.webm", {
                type: "video/webm",
            });
            this.uploader.addToQueue([file]);
        }

        this.uploader.queue[0].upload();
    }

    submitAssignmentData() {
        this.submitClicked = true;
        this.assignmentPayload = {
            assignmentID: this.assignment.assignmentID,
            studentVideo: this.studentVideoName,
            studentComments: this.feedbackForm.value.studentComments,
            studentScore: this.feedbackForm.value.studentScore.toString(),
            completed: true,
            // If this is an assignment and not an compositeTaskAssignment, this value will be null.
        };

        if (this.isComposite) {
            this.assignmentPayload.compositeTaskAssignmentID = (<
                CompositeTaskAssignment
            >this.assignment).compositeTaskAssignmentID;
        }

        this.submitAssignment.emit(this.assignmentPayload);
    }

    // maxAttempts from the class (if it exists), performTask.views from the asset
    formatMaxAttempts() {
        if (
            this.maxAttempts === -1 ||
            (this.performTask.views &&
                (this.performTask.views === 6 || this.performTask.views === -1))
        ) {
            return " as many times as you want";
        } else if (this.maxAttempts === 1 || this.performTask.views === 1) {
            return " up to " + this.maxAttempts + " time";
        } else {
            return (
                " up to " +
                (this.performTask.views || this.maxAttempts) +
                " times"
            );
        }
    }

    formatViewsRemaining() {
        if (this.viewsRemaining === -1) {
            return "unlimited views ";
        } else if (this.viewsRemaining === 1) {
            return "1 view ";
        } else {
            return this.viewsRemaining + " views ";
        }
    }

    // maxAttempts from the class (if it exists), performTask.recordingTries from the asset
    formatRecordingTries() {
        if (this.performTask.recordingTries === -1 || this.maxAttempts === -1) {
            return "unlimited tries ";
        } else if (
            this.performTask.recordingTries === 1 ||
            this.maxAttempts === 1
        ) {
            return "1 try ";
        } else {
            return (
                (this.performTask.recordingTries || this.maxAttempts) +
                " tries "
            );
        }
    }
}
